import { defineStore } from "pinia";
import { useICPStore } from "@/stores/icpStore";
import { useAuthStore } from "@/stores/authStores";
import api from "@/services/api";

export const useContactsStore = defineStore("contactsStore", {
    state: () => ({
        trains: [],
        handheld: [],
        selectedTrains: [],
        selectedHandheld: [],
        selectedPhoneContacts: [],

        // ISDN -> trainId map
        allTrainNumbersCached: {}
    }),

    getters: {
        // return an array like [{isdn: 123}, {isdn: 456}, ...] needed by sdk to subscribe to status changes
        getIsdnArray: (state) => {
            const array = [];
            state.handheld.forEach((item) => {
                array.push({ isdn: item.isdn });
            });
            return array;
        },
        getOnlineTrains: (state) => state.trains.filter((train) => train.status === "Online"),
        getOnlineHandHeld: (state) => state.handheld.filter((hh) => hh.status === "Online"),
        getAllSelected: (state) => [...state.selectedTrains, ...state.selectedHandheld]
    },

    actions: {
        updateContactsStatus(data) {
            data.forEach((item) => {
                // If ISDN belongs to a train cab, ignore it here (trains are handled elsewhere)
                const isTrainCab = Object.hasOwn(this.allTrainNumbersCached, item.isdn);
                if (isTrainCab) return;

                if (item.statustype === "15" || item.statustype === "16") {
                    // update handheld status
                    const handheld = this.handheld.find((handheld) => handheld.isdn === item.isdn);
                    if (handheld) {
                        handheld.status = item.statusvalue === "4011" ? "Online" : "Offline";

                        if (handheld.status === "Offline") {
                            const index = this.selectedHandheld.findIndex((hh) => hh.value.isdn === handheld.isdn);
                            if (index !== -1) {
                                // remove from selectedHandheld if it was selected
                                this.selectedHandheld.splice(index, 1);
                            }
                        }
                    }
                }
            });
        },

        setNetworkContacts(data) {
            if (data.list) {
                this.handheld = data.list
                    .filter(
                        (item) =>
                            item.isdn !== useAuthStore().loggedUser &&
                            // exclude train cab devices using map lookup
                            !Object.hasOwn(this.allTrainNumbersCached, item.isdn)
                    )
                    .filter((item) => item.category === "9")
                    .map((item) => ({
                        radioId: item.name,
                        isdn: item.isdn,
                        status: "Unknown"
                    }));

                useICPStore().ICP.dispatch.voice.subscribeUserStatus({
                    reslist: this.getIsdnArray,
                    callback: () => {}
                });
            }
        },

        fetchTrainsList() {
            api.get("/train/getOnlineTrains", {
                params: {
                    loggedUsername: useAuthStore().loggedUser
                }
            })
                .then((response) => {
                    this.trains = response.data;

                    // Filter out selected trains that are not online
                    this.selectedTrains = this.selectedTrains.filter((train) =>
                        this.trains.some((t) => t.trainId === train.value.trainId)
                    );
                })
                .catch((error) => {
                    console.error("There was an error fetching online trains: ", error);
                });
        },

        fetchTrainsCache() {
            api.get("/train/getAllTrains", { params: {} })
                .then((response) => {
                    // Build ISDN -> trainId lookup map (cab1 and cab2)
                    this.allTrainNumbersCached = Object.fromEntries(
                        response.data.flatMap((train) =>
                            [
                                [train.isdnCab1, train.trainSetId],
                                [train.isdnCab2, train.trainSetId]
                            ].filter(([isdn]) => isdn != null)
                        )
                    );

                    // Fetch online devices
                    useICPStore().ICP.dispatch.query.queryUserListV1({
                        offset: "0",
                        limit: "100",
                        callback: (data) => {
                            // use `this` store instance instead of calling useContactsStore() again
                            this.setNetworkContacts(data);
                        }
                    });
                })
                .catch((error) => {
                    console.error("There was an error fetching trains: ", error);
                });
        }
    }
});
